import React, { useState } from 'react';
import { Box, Flex, Button, IconButton, Image, Text, VStack, Separator } from '@chakra-ui/react';
import {
  DrawerRoot,
  DrawerBackdrop,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerCloseTrigger,
} from '@/components/ui/drawer';
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@/components/ui/popover';
import { RxHamburgerMenu } from 'react-icons/rx';
import { RiNotificationFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const Header = () => {
  const [open, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  return (
    <>
      {/* ノッチ部分 */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        height="env(safe-area-inset-top)"
        bg="teal.500"
        zIndex={10}
        width="100%"
      />

      {/* ヘッダー */}
      <Box
        as="header"
        position="fixed"
        top="env(safe-area-inset-top)"
        left={0}
        right={0}
        bg="teal.500"
        color="white"
        p={1}
        zIndex={9}
        maxW={'breakpoint-xl'}
        width="100%"
        mx="auto"
      >
        <Flex justify="space-between" align="center">
          {/* ハンバーガーメニュー */}
          <Box w={24}>
            <IconButton
              aria-label="Open Menu"
              colorScheme="teal"
              onClick={() => setOpen(true)}
              variant="plain"
            >
              <RxHamburgerMenu color="white" />
            </IconButton>
          </Box>
          {/* ロゴ */}
          <Image
            src="/img/BunPay-logo-sample5.png"
            alt="logo"
            w={24}
            pointerEvents="none"
            userSelect="none"
          />
          {/* 通知アイコン */}
          <Box w={24} display="flex" justifyContent="flex-end">
            <PopoverRoot>
              <PopoverTrigger asChild>
                <IconButton aria-label="Open Notifications" colorScheme="teal" variant="plain">
                  <RiNotificationFill color="white" />
                </IconButton>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverBody userSelect="none">
                  <Text textAlign="center">通知はありません</Text>
                </PopoverBody>
              </PopoverContent>
            </PopoverRoot>
          </Box>
        </Flex>
      </Box>

      <DrawerRoot open={open} onOpenChange={e => setOpen(e.open)} placement="start" size="xs">
        <DrawerBackdrop />
        <DrawerContent bg="white/95">
          <DrawerHeader borderBottom="1px" borderColor="gray.100">
            <DrawerTitle color="gray.700" fontSize="lg" fontWeight="bold">
              メニュー
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody py={4}>
            {isAuthenticated ? (
              // ログイン状態のメニュー
              <VStack align="stretch" gap={2}>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  h="auto"
                  fontWeight="medium"
                  color="gray.700"
                  _hover={{ bg: 'teal.50' }}
                  onClick={() => {
                    navigate('/main/home');
                    setOpen(false);
                  }}
                >
                  ホーム
                </Button>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  h="auto"
                  fontWeight="medium"
                  color="gray.700"
                  _hover={{ bg: 'teal.50' }}
                  onClick={() => {
                    navigate('/main/payments');
                    setOpen(false);
                  }}
                >
                  支払い
                </Button>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  h="auto"
                  fontWeight="medium"
                  color="gray.700"
                  _hover={{ bg: 'teal.50' }}
                  onClick={() => {
                    navigate('/main/profile');
                    setOpen(false);
                  }}
                >
                  プロフィール
                </Button>
                <Separator my={3} />
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  h="auto"
                  fontWeight="medium"
                  color="gray.700"
                  _hover={{ bg: 'teal.50' }}
                  onClick={() => {
                    navigate('/pwa-install');
                    setOpen(false);
                  }}
                >
                  PWAのインストール方法
                </Button>
                <Separator my={3} />
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  h="auto"
                  fontWeight="medium"
                  color="red.600"
                  _hover={{ bg: 'red.50' }}
                  onClick={async () => {
                    await logout();
                    setOpen(false);
                    navigate('/login');
                  }}
                >
                  ログアウト
                </Button>
              </VStack>
            ) : (
              // 未ログイン状態のメニュー
              <VStack align="stretch" gap={4}>
                <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                  BunPayをご利用いただくには、ログインまたはアカウントを作成してください。
                </Text>
                <Button
                  colorPalette="teal"
                  borderRadius="xl"
                  py={3}
                  h="auto"
                  onClick={() => {
                    navigate('/login');
                    setOpen(false);
                  }}
                >
                  ログイン
                </Button>
                <Button
                  variant="outline"
                  colorPalette="teal"
                  borderRadius="xl"
                  py={3}
                  h="auto"
                  onClick={() => {
                    navigate('/create-account');
                    setOpen(false);
                  }}
                >
                  アカウント作成
                </Button>
                <Separator my={3} />
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  h="auto"
                  fontWeight="medium"
                  color="gray.700"
                  _hover={{ bg: 'teal.50' }}
                  onClick={() => {
                    navigate('/pwa-install');
                    setOpen(false);
                  }}
                >
                  PWAのインストール方法
                </Button>
              </VStack>
            )}
          </DrawerBody>
          <DrawerCloseTrigger />
        </DrawerContent>
      </DrawerRoot>
    </>
  );
};

export default Header;
